<template>
    <ClientOnly>
        <VNavigationDrawer v-model="link.navDrawer" width="300" elevation="0" color="transparent" scrim temporary
            floating :order="-1" :style="`${store.deviceType === 'Desktop' ? 'backdrop-filter: blur(5px)' : ''};`">
            <VCard :style="store.deviceType === 'Desktop' ? 'opacity: 0.8;' : ''" height="100%" flat rounded="0">
                <VToolbar color="transparent">
                    <template #title>
                        <!-- AppLogo itself handles Home navigation/scrolling -->
                        <div @click="closeDrawer">
                            <AppLogo />
                        </div>
                    </template>

                    <template #append>
                        <VBtn class="mr-2" variant="flat" icon="$mdiClose" aria-label="Close navigation drawer"
                            @click="closeDrawer" />
                    </template>
                </VToolbar>

                <VList class="mx-1" density="compact" nav role="navigation" aria-label="Main Navigation">
                    <VListItem v-for="linkItem in linkItems" :key="linkItem.id" :aria-label="linkItem.text" rounded="lg"
                        :to="linkItem.scrollTo ? undefined : linkItem.to" color="primary" link :title="linkItem.text"
                        :active="linkItem.scrollTo
                                ? route.path === '/' && activeSection === linkItem.scrollTo
                                : undefined
                            " v-ripple="{ class: 'text-primary' }" @click="handleLinkClick(linkItem)">
                        <template #prepend>
                            <VIcon :icon="`$mdi${linkItem.icon}`" />
                        </template>
                    </VListItem>

                    <VDivider class="my-3" />

                    <VListItem rounded="pill" color="primary" variant="tonal" link :active="route.path === '/' &&
                        activeSection === consultationItem.scrollTo
                        " @click="handleLinkClick(consultationItem)">
                        <template #prepend>
                            <VIcon :icon="`$mdi${consultationItem.icon}`" />
                        </template>

                        <VListItemTitle class="font-weight-bold">
                            {{ consultationItem.text }}
                        </VListItemTitle>
                    </VListItem>
                </VList>
            </VCard>
        </VNavigationDrawer>
    </ClientOnly>
</template>

<script setup lang="ts">
import { useAppLink } from '@S/appLink'
import { useAppStore } from '@S/appStore'

type LinkItem = {
    id: number
    text: string
    icon: string
    to?: string
    scrollTo?: string
}

const link = useAppLink()
const store = useAppStore()

const route = useRoute()

/*
 * Homepage navigation sections.
 *
 * Home is intentionally NOT here.
 * AppLogo is now the Home control.
 */
const baseLinkItems = computed < LinkItem[] > (() => [
    {
        id: 2,
        text: 'Transformations',
        scrollTo: 'transformations',
        icon: 'ImageMultiple',
    },
    {
        id: 3,
        text: 'Testimonials',
        scrollTo: 'testimonials',
        icon: 'MessageText',
    },
])

/*
 * Consultation stays separate so it can be rendered as
 * the prominent CTA at the bottom of the nav.
 */
const consultationItem = computed < LinkItem > (() => ({
    id: 5,
    text: 'BOOK A CONSULTATION',
    scrollTo: 'contact',
    icon: 'LightningBolt',
}))

const linkItems = computed < LinkItem[] > (() => {
    const items: LinkItem[] = [...baseLinkItems.value]

    items.push({
        id: 4,
        text: 'Members Area',
        to: store.isAuthenticated
            ? '/dashboard'
            : '/auth/login',
        icon: 'AccountGroup',
    })

    return items
})

/*
 * Nothing is active at the top of the homepage.
 *
 * Once Transformations, Testimonials or Contact actually
 * reaches the viewport detection line, it becomes active.
 */
const activeSection = ref < string | null > (null)

const sectionIds = computed(() => [
    ...baseLinkItems.value
        .filter(item => item.scrollTo)
        .map(item => item.scrollTo as string),

    consultationItem.value.scrollTo as string,
])

const updateActiveSection = () => {
    /*
     * Section navigation only applies to the homepage.
     */
    if (route.path !== '/') {
        activeSection.value = null
        return
    }

    const appBar = document.getElementById('app-bar')
    const appBarHeight = appBar?.offsetHeight ?? 0

    /*
     * Detection line immediately beneath the app bar.
     */
    const probeY = appBarHeight + 40

    let currentSection: string | null = null

    for (const id of sectionIds.value) {
        const element = document.getElementById(id)

        if (!element) {
            continue
        }

        const rect = element.getBoundingClientRect()

        /*
         * Only activate the section when our detection line
         * is genuinely inside that section.
         */
        if (
            rect.top <= probeY &&
            rect.bottom > probeY
        ) {
            currentSection = id
            break
        }
    }

    /*
     * Contact may be too short to reach the probe line,
     * so make it active when we're at the very bottom.
     */
    const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2

    if (atBottom) {
        currentSection = consultationItem.value.scrollTo ?? null
    }

    activeSection.value = currentSection
}

const handleScroll = () => {
    updateActiveSection()
}

const handleResize = () => {
    updateActiveSection()
}

onMounted(() => {
    window.addEventListener('scroll', handleScroll, {
        passive: true,
    })

    window.addEventListener('resize', handleResize, {
        passive: true,
    })

    nextTick(() => {
        updateActiveSection()
    })
})

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', handleResize)
})

const closeDrawer = () => {
    link.navDrawer = false
}

const scrollTo = (id: string) => {
    const el = document.getElementById(id)

    if (!el) {
        return
    }

    el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
    })
}

/*
 * Used when navigating from another route.
 *
 * The hash allows Nuxt's router scroll behaviour to reliably
 * locate the homepage section after the page has mounted.
 */
const handleLinkClick = async (item: LinkItem) => {
    /*
     * Normal route, e.g. Members Area.
     *
     * :to handles the actual navigation.
     */
    if (!item.scrollTo) {
        closeDrawer()
        return
    }

    const target = item.scrollTo

    closeDrawer()

    /*
     * Already on homepage.
     *
     * Allow the drawer state to update first, then smoothly
     * scroll to the requested section.
     */
    if (route.path === '/') {
        await nextTick()

        requestAnimationFrame(() => {
            scrollTo(target)
        })

        return
    }

    /*
     * We're on another route.
     *
     * Navigate to the homepage with a temporary hash.
     */
    await navigateTo({
        path: '/',
        hash: `#${target}`,
    })

    /*
     * Wait for Nuxt/router smooth scrolling to finish.
     */
    await waitForScrollEnd()

    /*
     * Remove the temporary hash WITHOUT causing another
     * router navigation or changing the scroll position.
     */
    window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search
    )

    updateActiveSection()
}

const waitForScrollEnd = (): Promise<void> => {
    return new Promise(resolve => {
        let finished = false

        const finish = () => {
            if (finished) {
                return
            }

            finished = true

            window.removeEventListener('scrollend', finish)
            window.clearTimeout(timeout)

            resolve()
        }

        window.addEventListener(
            'scrollend',
            finish,
            { once: true }
        )

        /*
         * Fallback for browsers where scrollend isn't available
         * or doesn't fire for some reason.
         */
        const timeout = window.setTimeout(
            finish,
            1500
        )
    })
}
</script>